/**
 * 店内模块（R0：咖啡馆模板场景 + 选座/饮品/打招呼可见反馈；到访状态由 journey 模块的 VisitService 提供）。
 * 拍照不给主人点（VISIT-NO-PHOTO-BTN-01）：到访页滤掉 take_photo，演示卡片也不再写「合影」。
 */
import { Link } from "react-router";
import { defineModule, slot } from "@/shared/modules/types";
import type { JourneyOverlayProps } from "@/shared/slots/names";
import { Card, Icon } from "@/shared/ui";
import { mapFocusHref } from "@/features/world_map/mapFocus";
import { VisitPage } from "./VisitPage";
import { headingHome } from "./visitKind";

/**
 * 旅途卡片入口。快照里没有地点类型（是店、小路还是公园要打开到访才知道），live 的说法不写“到店 / 座位 / 饮品”；
 * 演示到访固定是示例咖啡馆，保留原说法。
 */
function VisitEntryCard({ snapshot, nowMs }: JourneyOverlayProps) {
  const visitId = snapshot.current_visit_id ?? snapshot.planned_visit_id;
  if (!visitId && snapshot.data_origin !== "fixture") return null;
  if (!visitId && !snapshot.arrival_context) return null;
  const atVenue = Boolean(snapshot.current_visit_id);
  // 到访结束、TA 在回家的路上（2026-09-25 巡检 R10-10）：原来仍写“这趟要去的地方 / 到了以后才能开始活动”。
  // 说法照地图面板（world_map/copy.ts 的“在回家的路上”）。点进去是地图并对准这只宠物——TA 还没到家、小窝里是空的，
  // 和小窝“我出门啦”便笺、地图面板“看看 TA”是同一种去处（world_map/mapFocus）。去的路上、到访中、演示到访都照旧。
  if (snapshot.data_origin !== "fixture" && headingHome(snapshot, nowMs)) {
    return (
      <Card>
        <Link to={mapFocusHref(snapshot.pet_id)} className="ps-journey-card">
          <span className="ps-journey-card__icon">
            <Icon name="home" />
          </span>
          <span style={{ flex: 1 }}>
            <strong>TA 在回家的路上</strong>
            <span className="ps-muted" style={{ display: "block" }}>
              看看 TA 在哪
            </span>
          </span>
          <Icon name="chevron" />
        </Link>
      </Card>
    );
  }
  return (
    <Card>
      <Link to={`/visits/${encodeURIComponent(visitId ?? "fx-visit-001")}`} className="ps-journey-card">
        <span className="ps-journey-card__icon">
          <Icon name="seat" />
        </span>
        <span style={{ flex: 1 }}>
          <strong>{snapshot.data_origin === "fixture" ? "看看 TA 在店里（演示到访）" : atVenue ? "TA 已经到了" : "这趟要去的地方"}</strong>
          {/* live 的副标题控制在一行（320 宽约 12 个字），不把一个词折成两半 */}
          <span className="ps-muted" style={{ display: "block" }}>
            {snapshot.data_origin === "fixture" ? "示例咖啡馆 · 选座、点饮品" : atVenue ? "去看看 TA 在那儿做什么" : "到了以后才能开始活动"}
          </span>
        </span>
        <Icon name="chevron" />
      </Link>
    </Card>
  );
}

export default defineModule({
  id: "venue",
  routes: [{ path: "visits/:visitId", element: <VisitPage /> }],
  slots: [slot("journey.cards", "venue.entry", VisitEntryCard, 20)],
});
